<template>
  <div class="tw-p-2">
    <h2 class="tw-text-xl tw-font-bold tw-tracking-tight tw-text-slate-800 tw-mb-1">Containers</h2>
    <p v-if="containersData" class="tw-text-sm tw-text-slate-500 tw-mb-4">
      {{ containersData.total }} containers
    </p>

    <div class="tw-mb-4 tw-flex tw-items-center">
      <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-slate-700 tw-cursor-pointer">
        <input 
          class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300 tw-text-slate-900 focus:tw-ring-slate-900" 
          type="checkbox" 
          v-model="showActiveOnly" 
        />
        Show Active Only
      </label>
    </div>

    <div class="tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white">
      <table class="tw-w-full tw-border-collapse tw-text-left tw-text-sm">
        <thead class="tw-bg-slate-50 tw-border-b tw-border-slate-200">
          <tr>
            <th class="tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Container</th>
            <th class="tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Active Shipment</th>
            <th class="tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Yard Status</th>
          </tr>
        </thead>
        <tbody v-if="containersData" class="tw-divide-y tw-divide-slate-100">
          <tr v-for="container in containerList" :key="`customer-container-${container.id}`" class="hover:tw-bg-slate-50/50">
            <td class="tw-px-4 tw-py-3 tw-font-medium tw-text-slate-900">
              {{ container.containerId != 'EXPUNDEF' ? container.containerId : 'Container Not Defined' }}
            </td>
            <td class="tw-px-4 tw-py-3">
              <span 
                v-if="container.isActive" 
                class="tw-inline-flex tw-items-center tw-rounded-md tw-bg-emerald-50 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-emerald-700 tw-ring-1 tw-ring-inset tw-ring-emerald-600/20"
              >
                Active
              </span>
              <span 
                v-else 
                class="tw-inline-flex tw-items-center tw-rounded-md tw-bg-slate-100 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-slate-600"
              >
                Archived
              </span>
            </td>
            <td class="tw-px-4 tw-py-3">
              <YardStatusLabel :container="container" :enableStale="false"></YardStatusLabel>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="containersData && containersData.pages > 1" class="tw-mt-4 tw-flex tw-justify-center">
      <PaginationBar 
        :page="containersPage" 
        :totalPages="containersData.pages" 
        @previousPage="updateContainersPage(-1)"
        @nextPage="updateContainersPage(1)" 
        @goToPage="goToContainersPage" 
      />
    </div>

    <div class="tw-my-8 tw-border-t tw-border-slate-100"></div>

    <h2 class="tw-text-xl tw-font-bold tw-tracking-tight tw-text-slate-800 tw-mb-4">Orders</h2>

    <div class="tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white">
      <table class="tw-w-full tw-border-collapse tw-text-left tw-text-sm">
        <thead class="tw-bg-slate-50 tw-border-b tw-border-slate-200">
          <tr>
            <th class="tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Order #</th>
            <th class="tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Bill of Lading</th>
            <th class="tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700"># Containers</th>
            <th class="tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Status</th>
            <th class="tw-px-4 tw-py-3"></th>
          </tr>
        </thead>
        <tbody v-if="ordersData" class="tw-divide-y tw-divide-slate-100">
          <tr v-for="order in ordersData.orders" :key="order.id" @click="selectOrder(order.id)" 
            class="tw-cursor-pointer hover:tw-bg-slate-50 tw-transition-colors">
            <td class="tw-px-4 tw-py-3 tw-font-bold tw-text-slate-900">#{{ order.id }}</td>
            <td class="tw-px-4 tw-py-3 tw-text-slate-600">
              <span v-if="order.billOfLading">{{ order.billOfLading.billOfLadingId }}</span>
              <span v-else class="tw-italic tw-text-slate-400">{{ order.bookingNumber || 'No B/L' }}</span>
            </td>
            <td class="tw-px-4 tw-py-3 tw-text-slate-600">
              {{ order.containers ? order.containers.length : 0 }}
            </td>
            <td class="tw-px-4 tw-py-3">
              <span 
                class="tw-inline-flex tw-items-center tw-rounded-md tw-px-2 tw-py-1 tw-text-xs tw-font-medium"
                :class="order.status === 'Complete' ? 'tw-bg-emerald-50 tw-text-emerald-700 tw-ring-1 tw-ring-inset tw-ring-emerald-600/20' : 'tw-bg-blue-50 tw-text-blue-700 tw-ring-1 tw-ring-inset tw-ring-blue-600/20'"
              >
                {{ order.status }}
              </span>
            </td>
            <td class="tw-px-4 tw-py-3 tw-text-right">
              <button class="tw-rounded-lg tw-bg-slate-800 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-slate-700 tw-transition-colors">
                Go to order
              </button>
            </td>
          </tr>
          <tr v-if="$apollo.queries.ordersData.loading">
            <td colspan="5" class="tw-px-4 tw-py-8 tw-text-center">
              <div class="tw-inline-block tw-h-6 tw-w-6 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-slate-900 tw-border-r-transparent"></div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="ordersData && ordersData.pages > 1" class="tw-mt-4 tw-flex tw-justify-center">
      <PaginationBar 
        :page="ordersPage" 
        :totalPages="ordersData.pages" 
        @previousPage="updateOrdersPage(-1)" 
        @nextPage="updateOrdersPage(1)" 
        @goToPage="goToOrdersPage" 
      />
    </div>
  </div>
</template>


<script lang="ts">
import { defineComponent } from 'vue';
import YardStatusLabel from '../UI/YardStatusLabel.vue';
import PaginationBar from '../core/PageElements/PaginationBar.vue';
import CustomerOrdersQuery from '@/gql/customers/CustomerOrders.gql';
import CustomerContainersQuery from '@/gql/customers/CustomerContainers.gql';

export default defineComponent({
  props: ['customer'],
  data() {
    return {
      ordersPage: 0,
      ordersPageSize: 10,
      ordersData: null,
      containersPage: 0,
      containersPageSize: 10,
      containersData: null,
      showActiveOnly: true,
    };
  },
  apollo: {
    ordersData: {
      query: CustomerOrdersQuery,
      skip() {
        return !this.customer || !this.customer.id;
      },
      variables() {
        return {
          id: this.customer.id,
          page: this.ordersPage,
          pageSize: this.ordersPageSize,
        };
      },
      update: data => data.customer?.orders,
    },
    containersData: {
      query: CustomerContainersQuery,
      skip() {
        return !this.customer || !this.customer.id;
      },
      variables() {
        return {
          id: this.customer.id,
          page: this.containersPage,
          pageSize: this.containersPageSize,
          activeOnly: this.showActiveOnly,
        };
      },
      update: data => data.customer?.containers,
    },
  },
  name: 'CustomerContainers',
  components: {
    YardStatusLabel,
    PaginationBar,
  },
  computed: {
    containerList(): any[] {
      if (!this.containersData || !this.containersData.containers) return [];
      return this.containersData.containers;
    },
  },
  methods: {
    selectOrder(orderId: number) {
      const vm = (this as any);
      vm.$router.push(`/orders/${orderId}`);
    },
    updateContainersPage(inc: number) {
      this.containersPage += inc;
    },
    goToContainersPage(idx: number) {
      this.containersPage = idx;
    },
    updateOrdersPage(inc: number) {
      this.ordersPage += inc;
    },
    goToOrdersPage(idx: number) {
      this.ordersPage = idx;
    },
  },
});
</script>

<style scoped>
.orderRow {
  cursor: pointer;
}
</style>